/**
 * The way in to both §4.8 views.
 *
 * "The failure mode of overrides is nobody being able to answer 'why can Sarah
 * see this.'" These two screens are the answer, and they are reachable from
 * every node and every person as well as from here — but a person who has just
 * been told "Sarah can see something she shouldn't" arrives with a name, not
 * with a route, so this page starts from a name.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { usePeople, useSpaceNodes, useSpaces } from '../../api/queries.ts';
import { PrecedenceLegend } from '../../components/decision.tsx';
import { Badge, RoleBadge, StatusBadge } from '../../components/ui/badge.tsx';
import { EmptyState, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Select } from '../../components/ui/field.tsx';
import { Card, CardHeader, PageHeader } from '../../components/ui/layout.tsx';

function NodeChooser() {
  const spaces = useSpaces();
  const [spaceId, setSpaceId] = useState<string>('');
  const nodes = useSpaceNodes(spaceId, 'document.view');

  const spaceOptions = (spaces.data?.spaces ?? []).map((space) => ({
    value: space.id,
    label: space.name,
    description: space.kind,
  }));

  return (
    <div className="flex flex-col gap-4 p-5">
      <Select
        label="Space"
        placeholder="Choose a space…"
        options={spaceOptions}
        selectedKey={spaceId || null}
        onSelectionChange={setSpaceId}
      />
      {spaceId ? (
        nodes.isPending ? (
          <Loading />
        ) : nodes.isError ? (
          <ErrorState error={nodes.error} />
        ) : nodes.data.nodes.length === 0 ? (
          <p className="text-sm text-tertiary">No nodes you can reach in that space.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary">
            {nodes.data.nodes.map((node) => (
              <li key={node.id}>
                <Link
                  to="/schema/access/nodes/$nodeId"
                  params={{ nodeId: node.id }}
                  className="block px-3 py-2 text-sm font-medium text-primary no-underline hover:bg-secondary"
                >
                  {node.title}
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

function PersonChooser() {
  const people = usePeople({});

  if (people.isPending) return <Loading />;
  if (people.isError) return <ErrorState error={people.error} className="m-5" />;
  if (people.data.people.length === 0) {
    return <EmptyState title="No people to ask about" />;
  }

  return (
    <ul className="flex flex-col divide-y divide-secondary">
      {people.data.people.map((person) => (
        <li key={person.id}>
          <Link
            to="/schema/access/people/$personId"
            params={{ personId: person.id }}
            className="flex flex-wrap items-center gap-2 px-5 py-2.5 no-underline hover:bg-secondary"
          >
            <span className="font-medium text-primary">{person.name}</span>
            {person.account ? (
              <>
                <RoleBadge role={person.account.role} />
                <StatusBadge status={person.account.status} />
              </>
            ) : (
              // No account, so no baseline row and nothing to resolve. Saying so
              // here saves opening the view to find every answer denied (§2.4).
              <Badge tone="outline">No access</Badge>
            )}
            <span className="ml-auto text-xs text-tertiary">{person.email}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function AccessIndexPage() {
  return (
    <>
      <PageHeader
        title="Effective access"
        description="Two views over the same resolver. Neither computes its own answer — an explain view that did would be a second implementation of the most dangerous query in the system, and the day it disagreed with enforcement is the day it would start lying about access rather than reporting it."
      />

      <Card>
        <h2 className="mb-2 text-md font-semibold text-primary">How a decision is reached</h2>
        <PrecedenceLegend />
        <p className="mt-3 text-sm text-tertiary">
          A specific allow on a document overrides a broad deny three levels up. That is the
          intuitive behaviour and the one people can reason about — and it is a deliberate
          trade-off, recorded in ADR 0003.
        </p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <CardHeader
            title="Per person"
            description="Everything this person can reach, and the grant that decided each one."
          />
          <PersonChooser />
        </Card>

        <Card padded={false}>
          <CardHeader
            title="Per node"
            description="Who can reach this, and why — which grant, at which level, with what precedence."
          />
          <NodeChooser />
        </Card>
      </div>
    </>
  );
}
